import { useState, useEffect } from "react";
import { Leaf, Zap, Activity, Download } from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT } from "@/contexts/LanguageContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const CO2ReportView = ({ clientId }) => {
  const t = useT();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const tokenRes = await axios.post(`${API}/clients/${clientId}/download-token`);
      const url = `${API}/clients/${clientId}/report/co2/pdf?token=${tokenRes.data.token}`;
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "CO2e_Report.pdf");
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get(`${API}/clients/${clientId}/report/co2`);
        setReport(response.data);
      } catch (error) {
        console.error("Error fetching CO2 report:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [clientId]);

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

  const depts = report?.departments || [];

  return (
    <div data-testid="co2-report-view">
      <div className="page-header flex-between">
        <div>
          <h2 className="page-title">{t("co2_report_title")}</h2>
          <p className="page-subtitle">{t("co2_report_subtitle")}</p>
          <p className="text-muted" style={{ fontSize: "var(--text-small)" }}>{t("generated")}: {report?.report_generated_at ? new Date(report.report_generated_at).toLocaleString() : "N/A"}</p>
        </div>
        <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={downloading} data-testid="download-co2-pdf">
          <Download size={18} /> {downloading ? "Downloading..." : `${t("download_report")} (PDF)`}
        </button>
      </div>

      {report?.client_info && (
        <div className="card mb-lg">
          <h4 className="card-title">{t("client_information")}</h4>
          <div className="form-row">
            <div><div className="text-muted" style={{ fontSize: "var(--text-small)" }}>{t("company")}</div><div>{report.client_info.company_name}</div></div>
            <div><div className="text-muted" style={{ fontSize: "var(--text-small)" }}>{t("contact")}</div><div>{report.client_info.contact_name}</div></div>
          </div>
        </div>
      )}

      <div className="stats-grid mb-lg">
        <div className="stat-card">
          <div className="stat-icon success"><Leaf size={24} /></div>
          <div className="stat-value">{report?.total_co2_kg?.toLocaleString()}</div>
          <div className="stat-label">{t("total_carbon_footprint")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info"><Zap size={24} /></div>
          <div className="stat-value">{report?.total_energy_based_co2_kg?.toLocaleString()}</div>
          <div className="stat-label">{t("energy_kwh")} CO2</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon accent"><Activity size={24} /></div>
          <div className="stat-value">{report?.emission_factor_used} kg/kWh</div>
          <div className="stat-label">Emission Factor</div>
        </div>
      </div>

      <div className="card mb-lg">
        <h4 className="card-title">{t("emission_breakdown")}</h4>
        <div className="table-container">
          <table className="table" data-testid="co2-report-table">
            <thead>
              <tr>
                <th>{t("department")}</th>
                <th>{t("co2_emission")} (Inv.)</th>
                <th>Energy CO2 (kg)</th>
                <th>IoT CO2 (kg)</th>
                <th>Total CO2 (kg)</th>
                <th>{t("equipment")}</th>
                <th>{t("audit_pct")}</th>
              </tr>
            </thead>
            <tbody>
              {depts.map((dept, idx) => (
                <tr key={idx}>
                  <td><strong>{dept.department}</strong></td>
                  <td>{dept.inventory_co2_kg?.toLocaleString()}</td>
                  <td>{dept.energy_based_co2_kg?.toLocaleString()}</td>
                  <td>{dept.iot_estimated_co2_kg?.toLocaleString()}</td>
                  <td><strong>{dept.total_co2_kg?.toLocaleString()}</strong></td>
                  <td>{dept.equipment_count}</td>
                  <td>{dept.audit_completion}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bento-grid mb-lg">
        <div className="card">
          <h4 className="card-title">{t("dept_emissions")}</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={depts.map(d => ({ name: d.department.split(" ")[0], co2: d.total_co2_kg }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
              <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 11 }} />
              <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="co2" fill="#C86B4F" name="CO2 (kg)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h4 className="card-title">CO2 Sources</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={depts.map(d => ({ name: d.department.split(" ")[0], inventory: d.inventory_co2_kg, iot: d.iot_estimated_co2_kg }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
              <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 11 }} />
              <YAxis tick={{ fill: "#526A5A", fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="inventory" fill="#244C36" name={t("inventory")} radius={[4, 4, 0, 0]} />
              <Bar dataKey="iot" fill="#3B82F6" name={t("iot_pmc")} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <h4 className="card-title">{t("recommendations")}</h4>
        <ul style={{ paddingLeft: "var(--space-lg)" }}>
          {report?.recommendations?.map((rec, i) => (
            <li key={i} style={{ marginBottom: "var(--space-sm)" }}>{rec}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CO2ReportView;
